import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FilterFilled,
  EditFilled,
  DeleteFilled,
  InfoCircleOutlined,
  CloseOutlined,
  ExportOutlined
} from "@ant-design/icons";
import { t } from "i18next";
import moment from "moment";
import {
  Row,
  Col,
  Table,
  Breadcrumb,
  Button,
  Input,
  SearchOutlined,
  PlusOutlined,
  Drawer,
  Form,
  DatePicker
} from "common/elements";
import { Util } from "common/util";

export default function withDataTable({
  title = "",
  addNewLink = "",
  addViewLink = "",
  updateLink = "",
  pathName = "/",
  dateFormat = "",
  datePlaceholder = "",
  pickerDate = "",
  columns = [],
  defaultPageSize = 20,
  permission = null,
  topFilterItems = [],
  getData = () => { },
  showFilter = false,
  showDate = false,
  showButtonInfo = false,
  showButtonDelete = true,
  showExport = false,
  showFetchListDate = true,
  tableScrollY = null,
  FormReview = () => { },
  FormInsertOrUpdate = () => { },
  buttonOption = () => { },
  handleAddNew = (modalRef) => { },
  handleShowUpdate = (id, modalRef) => { },
  handleShowDetail = (id, reviewRef) => { },
  confirmSoftDelete = async (id) => { },
  componentWillMount = () => { },
  handleSearch = (e) => { },
  filterFields = () => { },
}) {
  return () => {
    const [datas, setDatas] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [form] = Form.useForm();
    const params = new URLSearchParams(document.location.search);
    const navigate = useNavigate();
    let modalRef = React.createRef();
    let reviewRef = React.createRef();
    let timer = null;

    const fetchList = () => {
      setLoading(true);
      getData()
        .then((response) => {
          setDatas(response);
        })
        .catch(error => {
          if (error.response.status === 403) {
            localStorage.removeItem("ACCESS_TOKEN");
            navigate("/signin");
          }
        })
        .finally(() => setLoading(false));
    };

    const onSearch = (e) => {
      clearTimeout(timer);
      handleSearch(e);
      timer = setTimeout(() => {
        fetchList();
      }, 1000);
    };

    const onAddNew = () => {
      if (addNewLink) {
        navigate(addNewLink);
      } else {
        handleAddNew(modalRef);
      }
    };

    const onImport = (importLink) => {
      navigate(importLink);
    };

    const onShowDetail = (id) => {
      const profile = Util.getInstance().getCurrentUser();
      let hasNoPermission = false;
      if (permission && Util.getInstance().hasNoPermission("view", permission)) hasNoPermission = true;
      if (id === profile.id) hasNoPermission = false;

      if (hasNoPermission) {
        Util.getInstance().sweetAlertMessage(t("text_no_permission"), "warning");
      } else {
        if (addViewLink) {
          navigate(`${addViewLink}/${id}`);
        } else {
          handleShowDetail(id, reviewRef);
        }
      }
    };

    const onShowUpdateForm = (id) => {
      let hasNoPermission = false;
      if (permission && Util.getInstance().hasNoPermission("edit", permission)) {
        hasNoPermission = true;
      }

      if (hasNoPermission) {
        Util.getInstance().sweetAlertMessage(t("text_no_permission"), "warning");
      } else {
        if (updateLink) {
          navigate(`${updateLink}/${id}`);
        } else {
          handleShowUpdate(id, modalRef);
        }
      }
    };

    const onDelete = async (id) => {
      const confirmed = await Util.getInstance().sweetAlertConfirm(
        t("text_confrim"),
        "You won't be able to revert this!",
        ["Cancel", "Yes, delete it!"]
      );

      if (confirmed) {
        await confirmSoftDelete(id);
        Util.getInstance().publishEvent("fetchData");
        fetchList();
      }
    };

    const onTableChange = (newPagination, filters, sorter) => {
      fetchList({
        sortField: sorter.field,
        sortOrder: sorter.order,
        pagination: newPagination,
        ...filters,
      });
    };

    const onChangePagination = (page, pageSize) => {
      const params = new URLSearchParams(document.location.search);
      params.set("current", page);
      params.set("size", pageSize);
      Util.getInstance().pushParamsToURL(pathName, params.toString());
      fetchList();
    };

    const onShowSizeChange = (current, size) => {
      const params = new URLSearchParams(document.location.search);
      params.set("current", current);
      params.set("size", size);
      Util.getInstance().pushParamsToURL(pathName, params.toString());
      fetchList();
    };

    React.useMemo(() => {
      componentWillMount();
    }, []);

    React.useEffect(() => {
      fetchList();
      // eslint-disable-next-line
    }, []);
    const pagination = {
      current: params.has("current") ? parseInt(params.get("current")) : 1,
      defaultPageSize,
      pageSizeOptions: ["20", "30", "50", "100"],
      total: datas.pagination?.total,
      showSizeChanger: true,
      locale: { items_per_page: "" },
      onChange: onChangePagination,
      onShowSizeChange,
      showTotal: (total, range) => `${t("text_display")} ${range[0]}-${range[1]} ${t("text_out_of")} ${total}`,
    };


    return (
      <>
        <Row style={styles.breadCream}>
          <Col span={24}>
            <Breadcrumb>
              <Breadcrumb.Item
                onClick={() => navigate("/")}
                style={{ cursor: "pointer" }}
              >
                {t("text_dashboard")}
              </Breadcrumb.Item>
              <Breadcrumb.Item style={{ color: "#1d8747" }}>
                {title}
              </Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>

        <Row style={{ padding: 15 }}>
          <Col span={8}>
            <Title title={title} />
          </Col>
          <Col span={16} style={{ textAlign: "right", bottom: "5px" }}>
            <Input
              placeholder={t("text_search")}
              defaultValue={params.get("search")}
              prefix={<SearchOutlined />}
              style={styles.generalSearch}
              allowClear={true}
              onChange={onSearch}
            />

            {showDate && (
              <DatePickers
                fetchList={fetchList}
                pathName={pathName}
                dateFormat={dateFormat}
                datePlaceholder={datePlaceholder}
                showFetchListDate={showFetchListDate}
                pickerDate={pickerDate}
              />
            )}

            {Util.getInstance().hasPermission("import donor", "employee") && (
              <Button
                type="primary"
                className="btn-import"
                onClick={() => onImport("/employees/import-donor")}
              >
                {t("text_import_donor")}
              </Button>
            )}
            {React.useMemo(
              () => (
                <Filter
                  enable={showFilter}
                  filterFields={filterFields}
                  fetchList={fetchList}
                  pathName={pathName}
                />
              ),
              // eslint-disable-next-line
              []
            )}
            {React.useMemo(() => (<Export enable={showExport} filtExport={buttonOption} />), [])}
            {Util.getInstance().hasPermission("create", "employee") && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onAddNew}
                style={{ marginLeft: 10 }}
              >
                {t("text_add_new")}
              </Button>
            )}
          </Col>
        </Row>

        <div className="data-table" style={{ flex: 1, display: "block" }}>
          <Table
            dataSource={datas.data}
            pagination={pagination}
            onChange={onTableChange}
            rowKey={(record) => `${record.id}`}
            scroll={datas.pagination && datas.pagination.count > 10 ? { y: tableScrollY ? tableScrollY : window.innerHeight - (310 + (topFilterItems.length > 0 ? 60 : 0)) } : {}}
            columns={[
              ...columns,
              {
                title: t("text_action"),
                dataIndex: "id",
                key: "id",
                width: 100,
                render: (id) => {
                  return (
                    <div id="btn-action">
                      <div id="btnInfos" className="btn-infos">
                        {showButtonInfo && (
                          <Button
                            id="info-btn"
                            title={"Info"}
                            type="default"
                            className="info"
                            icon={<InfoCircleOutlined />}
                            onClick={() => onShowDetail(id)}
                            style={{
                              height: 35,
                              width: 35,
                              borderRadius: 35
                            }}
                          />
                        )}
                      </div>

                      <div id="btnUpdate" className="btn-update">
                        {Util.getInstance().hasPermission("edit", "employee") && (
                          <Button
                            style={{
                              marginLeft: 5,
                              height: 35,
                              width: 35,
                              borderRadius: 35,
                              border: "none"
                            }}
                            type="primary"
                            title={`${t("text_update")}`}
                            onClick={() => onShowUpdateForm(id)}
                            icon={<EditFilled style={{ color: "#fff" }} />}
                          />
                        )}
                      </div>

                      <div id="btnDelete" className="btn-delete" style={{ display: "none" }}>
                        {showButtonDelete && (
                          <Button
                            danger
                            title={t("text_delete")}
                            icon={<DeleteFilled style={{ color: "#fff" }} />}
                            shape="circle"
                            type="primary"
                            style={{
                              marginLeft: 5,
                              height: 35,
                              width: 35,
                              borderRadius: 35,
                              border: "none"
                            }}
                            onClick={() => onDelete(id)}
                          />
                        )}
                      </div>
                    </div>
                  );
                },
              },
            ]}
            loading={loading}
          />
        </div>

        <FormInsertOrUpdate
          ref={(f) => (modalRef = f)}
          onSuccess={fetchList}
          form={form}
          navigate={navigate}
        />
        <FormReview ref={(f) => (reviewRef = f)} onSuccess={fetchList} navigate={navigate} />
      </>
    );
  };
}

function Filter({ enable, filterFields, fetchList, pathName }) {
  const [visible, setVisible] = React.useState(false);
  const [form] = Form.useForm();
  const params = new URLSearchParams(window.location.search);
  const onShow = () => {
    setVisible(true);
  };

  React.useEffect(() => {
    if (params.get("dates")) {
      const rangeDates = params.get("dates").split(",");
      form.setFieldsValue({
        dates: [moment(rangeDates[0]), moment(rangeDates[1])],
      });
    }
    if (params.get("preset")) {
      form.setFieldsValue({
        taskPreset: parseInt(params.get("preset")),
      });
    }
    if (params.get("taskStatus")) {
      form.setFieldsValue({
        taskStatus: parseInt(params.get("taskStatus")),
      });
    }

    if (params.get("teamId")) {
      form.setFieldsValue({
        teamId: parseInt(params.get("teamId")),
      });
    }

    //eslint-disable-next-line
  }, []);

  const onReset = () => {
    const params = new URLSearchParams(window.location.search)

    if (params.get("dates")) {
      params.delete("dates");
      form.setFieldsValue({
        dates: undefined,
      });
    }

    Util.getInstance().pushParamsToURL(pathName, params.toString());

    Util.getInstance().publishEvent("resetFields");
    fetchList();
    setVisible(false);
  };

  const onApply = () => {
    fetchList();
    setVisible(false);
  };

  const onClose = () => {
    setVisible(false);
  };
  return (
    enable && (
      <>
        <Button
          icon={<FilterFilled />}
          onClick={onShow}
          style={{ marginLeft: 10 }}
        >
          {t("text_filter")}
        </Button>
        <Drawer
          title={
            <div style={styles.headerFilter}>
              <Button onClick={onReset}>{t("text_reset")}</Button>
              <div>{t("text_filter")}</div>
              <Button type="primary" onClick={onApply}>
                {t("text_apply")}
              </Button>
            </div>
          }
          placement="right"
          closable={false}
          onClose={onClose}
          visible={visible}
        >
          <Form style={{ margin: "-20px -20px 0 -20px" }} form={form}>
            {filterFields()}
          </Form>
        </Drawer>
      </>
    )
  );
}

function Title({ title }) {
  return <h4 style={{ marginBottom: 0, fontWeight: 600 }}>{title}</h4>;
}

function Export({ enable, filtExport }) {
  const [visible, setVisible] = React.useState(false);

  const onClose = () => {
    setVisible(false);
  };

  const onShow = () => {
    setVisible(true);
  };

  return (
    enable && (
      <>
        <Button
          icon={<ExportOutlined />}
          onClick={onShow}
          style={{ marginLeft: 10 }}
        >
          {t("text_export")}
        </Button>
        <Drawer
          title={
            <div style={styles.headerFilter}>
              <div>{t("text_export_employees")}</div>
              <div>
                <CloseOutlined onClick={onClose} />
              </div>
            </div>
          }
          placement="right"
          closable={false}
          onClose={onClose}
          visible={visible}
        >
          {visible ? <ExportExcelFile filtExport={filtExport} /> : null}
        </Drawer>
      </>
    )
  );
}

const ExportExcelFile = ({ filtExport }) => {
  return filtExport();
}

function DatePickers({
  pathName,
  fetchList,
  dateFormat,
  datePlaceholder,
  showFetchListDate,
  pickerDate,
}) {
  const util = new Util();
  const params = new URLSearchParams(document.location.search);
  const onChangeDate = (value) => {
    if (value) {
      params.set("date", moment(value).format("YYYY-MM-DD"));
    } else {
      params.delete("date");
    }

    util.pushParamsToURL(pathName, params.toString());
    if (showFetchListDate) fetchList();
  };

  return (
    <DatePicker
      name="month"
      placeholder={datePlaceholder}
      format={dateFormat}
      onChange={onChangeDate}
      style={{ width: "200px", marginLeft: "10px" }}
      picker={pickerDate}
      defaultValue={params.get("date") && moment(params.get("date"))}
    />
  );
}

const styles = {
  generalSearch: {
    width: 200,
  },
  headerFilter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  breadCream: {
    paddingBottom: 15,
    backgroundColor: "#EFF3F6",
  },
  style: {
    background: "#0092ff",
    padding: "8px 0",
  },
};
